import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { Shield, AlertTriangle, CheckCircle, FileText, Download, Eye, TrendingUp, Users, X } from 'lucide-react';
import toast from 'react-hot-toast';
import MockDataService from '../../../services/MockDataService';

const AuditorDashboard = () => {
  const [selectedModal, setSelectedModal] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [findings, setFindings] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadAuditorData = async () => {
      try {
        setLoading(true);
        setError(null);
        const [findingsResponse, reportsResponse] = await Promise.all([
          MockDataService.getAuditFindings(),
          MockDataService.getAuditReports?.() || Promise.resolve({ success: true, data: [] })
        ]);

        if (findingsResponse.success) {
          setFindings(findingsResponse.data || []);
        }
        if (reportsResponse.success) {
          setReports(reportsResponse.data || []);
        }
      } catch (err) {
        setError('Failed to load auditor data');
        console.error('Error loading auditor data:', err);
        // Fallback to default data
        setFindings([
          {
            id: 'finding-001',
            title: 'Weak Password Policy',
            severity: 'high',
            category: 'Security',
            status: 'open',
            discovered: '2025-01-15T10:30:00Z',
            description: 'Password policy does not meet minimum security requirements'
          },
          {
            id: 'finding-002',
            title: 'Missing Data Encryption',
            severity: 'medium',
            category: 'Compliance',
            status: 'in-progress',
            discovered: '2025-01-14T16:45:00Z',
            description: 'Sensitive data is not encrypted at rest'
          },
          {
            id: 'finding-003',
            title: 'Incomplete Audit Logs',
            severity: 'low',
            category: 'Governance',
            status: 'resolved',
            discovered: '2025-01-13T09:15:00Z',
            description: 'Some user actions are not being logged'
          }
        ]);
        setReports([
          {
            id: 'report-001',
            name: 'Security Compliance Report',
            type: 'Security',
            generated: '2025-01-15T08:00:00Z',
            findings: 12,
            status: 'completed'
          },
          {
            id: 'report-002',
            name: 'Data Privacy Assessment',
            type: 'Privacy',
            generated: '2025-01-14T14:30:00Z',
            findings: 8,
            status: 'completed'
          },
          {
            id: 'report-003',
            name: 'Access Control Review',
            type: 'Access',
            generated: '2025-01-13T11:15:00Z',
            findings: 5,
            status: 'in-progress'
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    loadAuditorData();
  }, []);

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'in-progress': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'resolved': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const handleStatsClick = (type) => {
    setSelectedModal(type);
  };

  const handleViewReport = (report) => {
    setSelectedReport(report);
    toast.success(`Opening ${report.name}...`);
  };

  const handleDownloadReport = (report) => {
    toast.success(`Downloading ${report.name}...`);
    // Simulate download
    //console.log('Downloading report:', report);
  };

  const handleExportReport = () => {
    toast.success('Exporting report... This will download in a moment.');
    // Simulate export
    setTimeout(() => {
      //console.log('Report exported');
    }, 1000);
  };

  const handleGenerateAudit = () => {
    toast.loading('Generating audit report...', { duration: 2000 });
    setTimeout(() => {
      toast.success('Audit report generated successfully!');
    }, 2000);
  };

  const closeModal = () => {
    setSelectedModal(null);
    setSelectedReport(null);
  };

  const getModalData = () => {
    switch (selectedModal) {
      case 'open':
        return {
          title: 'Open Findings',
          icon: <AlertTriangle className="h-6 w-6 text-red-500" />,
          items: findings.filter(f => f.status === 'open')
        };
      case 'in-progress':
        return {
          title: 'In Progress Findings',
          icon: <TrendingUp className="h-6 w-6 text-yellow-500" />,
          items: findings.filter(f => f.status === 'in-progress')
        };
      case 'resolved':
        return {
          title: 'Resolved Findings',
          icon: <CheckCircle className="h-6 w-6 text-green-500" />,
          items: findings.filter(f => f.status === 'resolved')
        };
      case 'reports':
        return {
          title: 'All Reports',
          icon: <FileText className="h-6 w-6 text-blue-500" />,
          items: reports
        };
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading auditor data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center">
        <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-500 text-lg font-semibold mb-2">Error: {error}</p>
        <Button onClick={() => window.location.reload()}>Retry</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-heading font-bold text-text-primary-light dark:text-text-primary-dark">
            Auditor Dashboard
          </h1>
          <p className="text-text-secondary-light dark:text-text-secondary-dark">
            Compliance monitoring and governance oversight
          </p>
        </div>
        <div className="flex space-x-3">
          <Button variant="outline" onClick={handleExportReport}>Export Report</Button>
          <Button onClick={handleGenerateAudit}>Generate Audit</Button>
        </div>
      </div>

      {/* Quick Stats - Clickable Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => handleStatsClick('open')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Open Findings</p>
                <p className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">
                  {findings.filter(f => f.status === 'open').length}
                </p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => handleStatsClick('in-progress')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">In Progress</p>
                <p className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">
                  {findings.filter(f => f.status === 'in-progress').length}
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => handleStatsClick('resolved')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Resolved</p>
                <p className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">
                  {findings.filter(f => f.status === 'resolved').length}
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => handleStatsClick('reports')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Reports Generated</p>
                <p className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">{reports.length}</p>
              </div>
              <FileText className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Findings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <AlertTriangle className="h-5 w-5" />
              <span>Recent Findings</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {findings.map((finding) => (
                <div key={finding.id} className="border border-border-light dark:border-border-dark rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-text-primary-light dark:text-text-primary-dark">
                      {finding.title}
                    </h3>
                    <div className="flex space-x-2">
                      <Badge className={getSeverityColor(finding.severity)}>
                        {finding.severity}
                      </Badge>
                      <Badge className={getStatusColor(finding.status)}>
                        {finding.status}
                      </Badge>
                    </div>
                  </div>
                  <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-2">
                    {finding.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <Badge variant="secondary">{finding.category}</Badge>
                    <span className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                      {new Date(finding.discovered).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recent Reports */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <FileText className="h-5 w-5" />
              <span>Recent Reports</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {reports.map((report) => (
                <div key={report.id} className="border border-border-light dark:border-border-dark rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-text-primary-light dark:text-text-primary-dark">
                      {report.name}
                    </h3>
                    <Badge className={report.status === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'}>
                      {report.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-2">
                    {report.findings} findings • {report.type}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                      Generated: {new Date(report.generated).toLocaleDateString()}
                    </span>
                    <div className="flex space-x-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleViewReport(report)}
                        title="View Report"
                      >
                        <Eye size={16} />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDownloadReport(report)}
                        title="Download Report"
                      >
                        <Download size={16} />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Compliance Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Shield className="h-5 w-5" />
            <span>Compliance Overview</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="font-medium text-text-primary-light dark:text-text-primary-dark mb-1">
                Security Compliance
              </h3>
              <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                85% compliant
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-100 dark:bg-yellow-900 rounded-full flex items-center justify-center mx-auto mb-3">
                <TrendingUp className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
              </div>
              <h3 className="font-medium text-text-primary-light dark:text-text-primary-dark mb-1">
                Data Privacy
              </h3>
              <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                92% compliant
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-3">
                <Users className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="font-medium text-text-primary-light dark:text-text-primary-dark mb-1">
                Access Control
              </h3>
              <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                78% compliant
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Modal for Stats Details */}
      {selectedModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={closeModal}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-3">
                {getModalData()?.icon}
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {getModalData()?.title}
                </h2>
              </div>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {getModalData()?.items.length === 0 ? (
                <p className="text-center text-gray-500 dark:text-gray-400 py-8">
                  No items found
                </p>
              ) : (
                <div className="space-y-4">
                  {getModalData()?.items.map((item) => (
                    <div
                      key={item.id}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-gray-900 dark:text-white">
                          {item.title || item.name}
                        </h3>
                        <div className="flex space-x-2">
                          {item.severity && (
                            <Badge className={getSeverityColor(item.severity)}>
                              {item.severity}
                            </Badge>
                          )}
                          {item.status && (
                            <Badge className={getStatusColor(item.status)}>
                              {item.status}
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      {item.description && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                          {item.description}
                        </p>
                      )}
                      
                      {item.type && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                          {item.findings} findings • {item.type}
                        </p>
                      )}
                      
                      <div className="flex items-center justify-between">
                        {item.category && (
                          <Badge variant="secondary">{item.category}</Badge>
                        )}
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {item.discovered && `Discovered: ${new Date(item.discovered).toLocaleDateString()}`}
                          {item.generated && `Generated: ${new Date(item.generated).toLocaleDateString()}`}
                        </span>
                      </div>
                      
                      {item.type && (
                        <div className="flex space-x-2 mt-3">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleViewReport(item)}
                          >
                            <Eye size={14} className="mr-1" />
                            View
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleDownloadReport(item)}
                          >
                            <Download size={14} className="mr-1" />
                            Download
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="flex justify-end p-6 border-t border-gray-200 dark:border-gray-700">
              <Button variant="outline" onClick={closeModal}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal for Report View */}
      {selectedReport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={closeModal}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                {selectedReport.name}
              </h2>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Report Type</label>
                  <p className="text-gray-900 dark:text-white">{selectedReport.type}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
                  <p><Badge className={getStatusColor(selectedReport.status)}>{selectedReport.status}</Badge></p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Total Findings</label>
                  <p className="text-gray-900 dark:text-white">{selectedReport.findings}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Generated On</label>
                  <p className="text-gray-900 dark:text-white">{new Date(selectedReport.generated).toLocaleString()}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Report ID</label>
                  <p className="text-gray-900 dark:text-white font-mono text-sm">{selectedReport.id}</p>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <Button variant="outline" onClick={closeModal}>
                Close
              </Button>
              <Button onClick={() => handleDownloadReport(selectedReport)}>
                <Download size={16} className="mr-2" />
                Download
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditorDashboard;
